import Alpine from '@alpinejs/csp';
import AsyncAlpine from 'async-alpine';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';
import focus from '@alpinejs/focus';
import { createRouter, PAGE_VIEW_EVENT } from './router/swup.js';
import { initScrollReveals } from './transitions/scroll-reveal.js';
import { initColourReveal } from './components/colour-reveal.js';
import { initEnquiryContext } from './components/enquiry-context.js';
import { initNavActiveSync } from './components/nav-active-sync.js';
import { initPageviewTracking } from './analytics/virtual-pageview.js';
import { initEventTracking } from './analytics/events.js';

// Level 2 component imports (eagerly loaded, registered before Alpine.start())
import accordion from './components/accordion.js';
import mobileNav from './components/mobile-nav.js';
import announcementBar from './components/announcement-bar.js';
import themeToggle from './components/theme-toggle.js';
import toastContainer from './components/toast-container.js';
import video from './components/video.js';
import carousel from './components/carousel.js';
import share from './components/share.js';
import loadMore from './components/load-more.js';
// The booking embed's postMessage channel. Imported into the CRITICAL PATH, not async,
// because the /contact/ panel is server-rendered with a real `src` and starts loading
// before Alpine runs — the resize message can arrive at any moment after that, and an
// async island would drop the ones that land first. It is ~1 KB.
import bookingDrawer from './components/booking-drawer.js';
import clinikoEmbed from './components/cliniko-embed.js';
// Library tier (inert): unwired in kit chrome, CSS only in docs.css. Registered so
// its /_docs demo route works with the real bundle; opt in per project. See anchor-nav.js.
import anchorNav from './components/anchor-nav.js';
import headroomHeader from './components/headroom-header.js';

/*
 * PSD-Stack — App Bootstrap
 *
 * Initialisation order matters:
 * 1. Alpine plugins (must register before Alpine.start())
 * 2. Alpine stores (must register before Alpine.start())
 * 3. Alpine.data() component registrations
 * 4. Async Alpine (must init before Alpine.start())
 * 5. Alpine.start()
 * 6. Swup router (after DOM is ready)
 * 7. Analytics (pageview tracking + structural events + deferred GTM)
 * 8. Deferred: GSAP (requestIdleCallback)
 * 9. Deferred: Three.js (desktop only, requestIdleCallback)
 */

// 1. Register Alpine plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);
Alpine.plugin(focus);

// 2. Register Alpine stores (persist across Swup page transitions)
Alpine.store('nav', { mobileOpen: false });

Alpine.store('toasts', {
    items: [],
    counter: 0,

    add(message, type = 'info', duration = 5000) {
        const id = ++this.counter;
        this.items.push({ id, message, type });

        if (duration > 0) {
            setTimeout(() => this.remove(id), duration);
        }
    },

    remove(id) {
        this.items = this.items.filter((t) => t.id !== id);
    },

    success(message) {
        this.add(message, 'success');
    },

    error(message) {
        this.add(message, 'error', 0);
    },

    warning(message) {
        this.add(message, 'warning');
    },
});

// 3. Register Level 2 component definitions
Alpine.data('accordion', accordion);
Alpine.data('mobileNav', mobileNav);
Alpine.data('announcementBar', announcementBar);
Alpine.data('themeToggle', themeToggle);
Alpine.data('toastContainer', toastContainer);
Alpine.data('video', video);
Alpine.data('carousel', carousel);
Alpine.data('share', share);
Alpine.data('loadMore', loadMore);
Alpine.data('clinikoEmbed', clinikoEmbed);
Alpine.data('bookingDrawer', bookingDrawer);
Alpine.data('anchorNav', anchorNav); // library tier — see import note above
Alpine.data('headroomHeader', headroomHeader); // library tier — see import note above

// 4. Initialise Async Alpine (lazy component loading)
Alpine.plugin(AsyncAlpine);
Alpine.asyncData('gallery', () => import('./components/gallery.js'));
Alpine.asyncData('form', () => import('./components/form.js'));

// 5. Start Alpine
Alpine.start();

// Make Alpine available globally for debugging
window.Alpine = Alpine;

// 6. Initialise Swup router
const swup = createRouter();

// Re-init Alpine on Swup page navigation
swup.hooks.before('content:replace', () => {
    Alpine.destroyTree(document.querySelector('#swup'));
});
swup.hooks.on('content:replace', () => {
    Alpine.initTree(document.querySelector('#swup'));
});

// 7. Analytics
initPageviewTracking(swup);
initEventTracking(swup);

// Colour-reveal image placeholders. GSAP-free (plain IntersectionObserver + CSS
// opacity), so it runs on first paint without waiting for the deferred GSAP
// bundle, and re-runs on every psd:page-view to enhance Swup-swapped content.
initColourReveal();
window.addEventListener(PAGE_VIEW_EVENT, initColourReveal);

// Enquiry-context hydration. Server-first "enquiry store": copies the per-page
// #enquiry-context values (plus once-captured UTM params) into hidden inputs of
// every POST form on load and on every psd:page-view — covering both forms inside
// #swup and a persistent global modal form outside it. Plain module, not an Alpine
// directive (CSP-safe). See components/enquiry-context.js.
initEnquiryContext();
window.addEventListener(PAGE_VIEW_EVENT, initEnquiryContext);

// Nav active-link sync. The header/nav renders outside #swup, so its
// server-rendered aria-current + active styling freeze after the first soft
// navigation; this re-derives the active link from the URL. Self-registers its
// listeners — the router is passed so it can also sync at content:replace, which
// is the difference between the highlight moving with the page and moving half a
// second after it. See components/nav-active-sync.js.
initNavActiveSync(swup);

// Defer GTM to idle — Tier 4 asset
if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
        import('./analytics/gtm.js').then(({ loadGTM }) => {
            loadGTM(import.meta.env.VITE_GTM_ID);
        });
    });
} else {
    setTimeout(() => {
        import('./analytics/gtm.js').then(({ loadGTM }) => {
            loadGTM(import.meta.env.VITE_GTM_ID);
        });
    }, 2000);
}

// ScrollTrigger lifecycle — kill instances before content swap, refresh after
swup.hooks.on('content:replace', () => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach((st) => st.kill());
    }
});

swup.hooks.on('page:view', () => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
    }
    initScrollReveals();
});

// 8. Deferred: GSAP + ScrollTrigger (loaded on first idle moment)
function loadGsap() {
    Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
        import('gsap/Flip'),
    ]).then(([{ gsap }, { ScrollTrigger }, { Flip }]) => {
        gsap.registerPlugin(ScrollTrigger, Flip);
        window.gsap = gsap;
        window.ScrollTrigger = ScrollTrigger;
        window.Flip = Flip;
        initScrollReveals();
    });
}

if ('requestIdleCallback' in window) {
    requestIdleCallback(loadGsap);
} else {
    setTimeout(loadGsap, 1000);
}

// 9. Deferred: Shader system (capability-gated, not viewport-gated)
// Runs GPU probe → if capable, initialises RenderManager + bridges + demo effects
if ('requestIdleCallback' in window) {
    requestIdleCallback(async () => {
        try {
            const { runProbe } = await import('./shaders/probe.js');
            const probeResult = await runProbe();

            if (!probeResult.capable) return;

            const { createRenderManager } = await import('./shaders/manager.js');
            const manager = createRenderManager(probeResult);

            // Init bridges (each guards against missing dependencies)
            const { initSwupBridge } = await import('./shaders/bridges/swup-bridge.js');
            initSwupBridge(manager, swup);

            const { initGsapBridge } = await import('./shaders/bridges/gsap-bridge.js');
            initGsapBridge(manager);

            // Register effects — each targets a <canvas> element in the DOM
            // CLEAR-LATER [DEMO] (build step 12 · de-kit §4.7): see documents/reference/clear-later-register.md
            //
            // The NoiseBgEffect registration is REMOVED WITH THE `hero` BLOCK. Its target,
            // `#hero-shader`, was emitted by that block's partial and by nothing else — the
            // template hero (partials/_hero) has no shader canvas. A registration whose
            // target never exists is not harmless: RenderManager resolves the selector at
            // boot and the effect throws on every page.
            //
            // RenderManager itself STAYS. ADR 2026-06-24-keep-webgl-background reversed a
            // premature full shader strip once already; the machinery is kept, only this
            // one effect's registration is dropped.

            const { DisplacementTransitionEffect } = await import('./shaders/effects/displacement-transition.js');
            manager.register(new DisplacementTransitionEffect(), { target: '#shader-canvas' });

            // Expose for dev tools
            if (import.meta.env.DEV) {
                window.__shaderManager = manager;
            }
        } catch (err) {
            if (import.meta.env.DEV) {
                console.warn('[Shaders] Initialisation failed:', err);
            }
        }
    });
}
